<template>
    <div data-page class="flex flex-col flex-1">
        <Banner v-if="!isArticleType" />

        <template v-if="isArticleType">
            <component :is="articleHeaderComponent" />
        </template>

        <section v-if="breadcrumbs" :data-mode="firstBlockSettings?.mode" class="full-width-bg surface-primary">
            <div 
                class="container relative w-full mx-auto px-4 py-6 lg:px-0"
                :class="{
                    [firstBlockSettings?.size]: firstBlockSettings?.size,
                    'py-6': ['brand', 'brand-invert'].includes(firstBlockSettings?.mode),
                    'pt-6': !['brand', 'brand-invert'].includes(firstBlockSettings?.mode),
                }"
            >
                <DBreadcrumbs
                    class="py-3"
                    :color="breadcrumbSettings?.color === 'brand' ? 'brand' : 'base'"
                    :slash-separator="breadcrumbSettings?.dividerStyle === 'slash'"
                    :top-line="breadcrumbSettings?.topLine"
                    :bottom-line="breadcrumbSettings?.bottomLine"
                >
                    <DBreadcrumb
                        v-if="breadcrumbSettings?.homeLink === 'icon'"
                        :href="homeUri"
                    />
                    <DBreadcrumb v-else :href="homeUri">
                        Home
                    </DBreadcrumb>

                    <DBreadcrumb
                        v-for="(breadcrumb, index) in breadcrumbs" :key="index"
                        :href="breadcrumb.href"
                    >
                        {{ breadcrumb.text }}
                    </DBreadcrumb>
                </DBreadcrumbs>
            </div>
        </section>

        <Blocks />

        <template v-if="isArticleType">
            <component :is="articleFooterComponent" />
        </template>

        <div class="mt-auto" data-prefooter>
            <PreFooter />
        </div>

        <Popup />
    </div>
</template>

<script setup>
    import { DBreadcrumbs, DBreadcrumb } from '@digistorm/spark'
    const entryStore = useEntryStore()
    const globalStore = useGlobalStore()

    const { entry, seomatic } = storeToRefs(entryStore)
    const { uri, digistormSettings, homeUri, getNavigation } = storeToRefs(globalStore)
    const breadcrumbSettings = get(digistormSettings.value, 'breadcrumbs', {})

    const articleTypes = ref(['ds_News_Entry', 'ds_Events_Entry'])

    const isArticleType = computed(() => {
        return articleTypes.value.includes(entry.value?.__typename)
    })

    // load page data
    const entryLoaded = ref(false)
    
    try {
        await entryStore.loadEntry(uri.value)
        entryLoaded.value = true
    } catch (error) {
        // Use showError to programmatically show error page
        await showError({
            statusCode: error.statusCode || 404,
            statusMessage: error.statusMessage || 'Page not found',
            message: error.message || 'The page you are looking for does not exist.'
        })
    }

    const articleHeaderComponent = defineAsyncComponent(() => {
        if (!isArticleType.value || !entryLoaded.value) {
            return
        }

        let name = entry.value.__typename

        // remove _ds and _Entry from the name
        name = replace(name, 'ds_', '')
        name = replace(name, '_Entry', '')

        return import(`~/components/article-types/${name}/header.async.vue`)
    })

    const articleFooterComponent = defineAsyncComponent(() => {
        if (!isArticleType.value || !entryLoaded.value) {
            return
        }

        let name = entry.value.__typename

        // remove _ds and _Entry from the name
        name = replace(name, 'ds_', '')
        name = replace(name, '_Entry', '')

        return import(`~/components/article-types/${name}/footer.async.vue`)
    })

    const seoTitle = computed(() => {
        if (!entryLoaded.value) return ''
        const titleContainer = JSON.parse(seomatic.value?.metaTitleContainer ?? '{}')
        return get(titleContainer, 'title.title', entry.value?.title)
    })

    const metaTags = computed(() => {
        if (!entryLoaded.value) return []
        return flatMap(JSON.parse(seomatic.value?.metaTagContainer ?? '[]'))
    })

    /* Get the color mode and size of the first block for use in breadcrumbs */
    const firstBlockSettings = computed(() => {
        if (!entryLoaded.value) return null

        const firstBlock = first(get(entry.value, 'contentBuilder', []))

        if (!firstBlock) return null

        const blockPrefix = Object.keys(firstBlock).find(key => key.endsWith('ColorPalette'))?.replace('_ColorPalette', '')

        const colorPalette = get(firstBlock, `${blockPrefix}_ColorPalette`, null)
        const containerWidth = get(firstBlock, `${blockPrefix}_ContainerWidth`, 'wide')

        const mode = (() => {
            switch (colorPalette) {
                case 'color-subtle':
                    return 'brand'
                case 'color-invert':
                    return 'brand-invert'
                default:
                    return colorPalette
            }
        })()

        const size = (() => {
            switch (containerWidth) {
                case 'full-width':
                    return ''
                case 'wide':
                    return 'container-wide'
                case 'extra-wide':
                    return 'container-extra-wide'
                default:
                    return 'container'
            }
        })()

        return {
            mode,
            size,
        }
    })

    const breadcrumbs = computed(() => {
        if (!entryLoaded.value) return null

        const showBreadcrumbs = get(entry.value, 'showBreadcrumbs', false)

        if (!breadcrumbSettings?.enabled || !showBreadcrumbs) {
            return null
        }

        const entries = getNavigation.value

        // Helper function to find page path in navigation
        function findPagePath(entries, targetUri, path = []) {
            for (const entry of entries) {
                const currentPath = [...path, entry]
                
                if (entry.uri === targetUri) {
                    return currentPath
                }
                
                if (entry.children?.length > 0) {
                    const childPath = findPagePath(entry.children, targetUri, currentPath)
                    if (childPath) return childPath
                }
            }
            return null
        }

        const pagePath = findPagePath(entries, uri.value)
    
        if (!pagePath) {
            return null 
        }

        return pagePath.map((page, index) => ({
            text: page.title,
            href: index === pagePath.length - 1 ? null : `/${page.uri}`,
        }))
    })

    useSeoMeta({
        title: seoTitle.value,
    })

    useHead({
        meta: metaTags.value
    })

</script>
